// MyVendors — iter101 dedicated page for /my-vendors.
//
// Shows only the user's saved vendors. Each card: avatar (photo or initials),
// name, type/category, green WhatsApp dot indicator, "Chat" CTA when
// WhatsApp is available.
//
// Sources:
//   • Logged-in → GET /api/favorites (target_type=vendor) + per-vendor lookup.
//   • Guest     → guestStore.getSaved() filtered to target_type=vendor.
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Users, Loader2, MessageCircle, User } from "lucide-react";
import { api, getMe } from "../lib/api";
import { BottomNav } from "../components/BottomNav";
import { useI18n } from "../lib/i18n";
import { sendVendorContact } from "../lib/sendRideRequest";
import { EmptyState } from "./MyFavorites";
import * as guestStore from "../lib/guestStorage";

export default function MyVendors() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const me = await getMe();
      if (cancelled) return;
      if (!me) {
        const saved = guestStore.getSaved().filter((s) => s.target_type === "vendor");
        const rows = await Promise.all(saved.map((s) =>
          api.get(`/vendors/${s.target_id}`)
            .then((r) => ({ ...r.data, _savedAt: s.saved_at }))
            .catch(() => null),
        ));
        if (cancelled) return;
        setItems(rows.filter(Boolean).sort((a, b) => (b._savedAt || "").localeCompare(a._savedAt || "")));
        setLoading(false);
        return;
      }
      try {
        const { data: favs } = await api.get("/favorites");
        const venFavs = (favs || []).filter((f) => f.target_type === "vendor");
        const rows = await Promise.all(venFavs.map((f) =>
          api.get(`/vendors/${f.target_id}`)
            .then((r) => ({ ...r.data, _savedAt: f.created_at }))
            .catch(() => null),
        ));
        if (cancelled) return;
        setItems(rows.filter(Boolean).sort((a, b) => (b._savedAt || "").localeCompare(a._savedAt || "")));
      } catch { /* ignore */ }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <main data-testid="my-vendors-page" className="min-h-screen pb-28" style={{ background: "#0B0B0B" }}>
      <header className="px-5 pt-6 pb-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} aria-label="Back" data-testid="my-vendors-back"
          className="grid place-items-center"
          style={{ minHeight: 48, minWidth: 48, marginLeft: -8 }}>
          <ArrowLeft className="w-5 h-5 text-foreground/80" strokeWidth={1.5} />
        </button>
        <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, color: "#F5F1E8" }}>
          {t("page_my_vendors_title")}
        </h1>
      </header>

      {loading ? (
        <div className="px-5 mt-10 text-center">
          <Loader2 className="w-5 h-5 animate-spin mx-auto text-foreground/40" />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Users}
          message={t("page_my_vendors_empty")}
          testid="my-vendors-empty"
        />
      ) : (
        <ul className="px-5 mt-6 space-y-2.5">
          {items.map((v) => {
            const hasWa = !!(v.whatsapp && v.whatsapp.replace(/\D/g, ""));
            return (
              <li key={v.id} className="lux-card p-3 flex items-center gap-3" data-testid={`my-vendors-${v.id}`}>
                <VendorAvatar vendor={v} />
                <div className="flex-1 min-w-0">
                  <Link to={`/vendors/${v.id}`} className="font-display text-base text-foreground truncate block">
                    {v.full_name}
                  </Link>
                  <div className="text-[11px] text-foreground/55 mt-0.5 flex items-center gap-2">
                    <span>{prettyCategory(v.category) || "Vendor"}</span>
                    {hasWa && (
                      <span
                        aria-label="WhatsApp available"
                        data-testid={`my-vendors-${v.id}-wa-dot`}
                        style={{
                          width: 8, height: 8, borderRadius: 999,
                          background: "#25D366",
                          boxShadow: "0 0 6px rgba(37,211,102,0.45)",
                          display: "inline-block",
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </div>
                </div>
                {hasWa && (
                  <button
                    type="button"
                    data-testid={`my-vendors-${v.id}-chat`}
                    aria-label="Chat"
                    onClick={(e) => {
                      e.preventDefault(); e.stopPropagation();
                      sendVendorContact(
                        { name: v.full_name || v.name, whatsapp: v.whatsapp, vendor_id: v.id },
                        { source: "my-vendors" },
                      );
                    }}
                    className="inline-flex items-center justify-center gap-1.5"
                    style={{
                      minHeight: 40,
                      padding: "0 14px",
                      borderRadius: 999,
                      background: "#25D366",
                      color: "#FFFFFF",
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    <MessageCircle className="w-3.5 h-3.5" strokeWidth={2} />
                    {t("page_my_vendors_chat")}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <BottomNav />
    </main>
  );
}

function VendorAvatar({ vendor }) {
  const photo = vendor.photos?.[0];
  const photoSrc = photo?.thumbnail || photo?.medium || photo?.url
    || vendor.selfie_photo?.thumbnail || vendor.selfie_photo?.medium;
  const url = photoSrc
    ? (photoSrc.startsWith?.("http") ? photoSrc : `${process.env.REACT_APP_BACKEND_URL}${photoSrc}`)
    : null;
  if (url) {
    return (
      <img src={url} alt=""
        className="w-12 h-12 rounded-full object-cover ring-1 ring-[rgba(232,184,75,0.4)] flex-shrink-0" />
    );
  }
  const initials = (vendor.full_name || vendor.name || "?").slice(0, 2).toUpperCase();
  return (
    <span
      className="grid place-items-center rounded-full flex-shrink-0"
      style={{
        width: 48, height: 48,
        background: "#171717",
        color: "#E8B84B",
        fontFamily: "'DM Sans', sans-serif",
        fontSize: 14, fontWeight: 600,
        border: "1px solid rgba(232,184,75,0.35)",
      }}
    >
      {initials || <User className="w-5 h-5" strokeWidth={1.5} />}
    </span>
  );
}

function prettyCategory(c) {
  return (c || "").replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
}
